import type {
  Operation,
  OperationLink,
  OperationResultEnvelope,
  TRPCClientError,
  TRPCLink,
} from "@trpc/client";
import type { AnyRouter } from "@trpc/server";
import { observable, type Observer } from "@trpc/server/observable";
import { captureAnalyticsEvent } from "../../posthog";

/** Expected outcomes of normal use: expired sessions refresh, throttled requests retry. */
const UNTRACKED_ERROR_CODES = new Set(["UNAUTHORIZED", "TOO_MANY_REQUESTS"]);

function getErrorCode(error: unknown): string {
  if (error !== null && typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (data !== null && typeof data === "object" && "code" in data) {
      const code = (data as { code?: unknown }).code;
      if (typeof code === "string") {
        return code;
      }
    }
  }
  return "CLIENT_ERROR";
}

/** Reports one operation exactly once, whichever of result or error arrives first. */
function createOutcomeRecorder(op: Operation) {
  const startedAt = performance.now();
  let recorded = false;

  return (outcome: "succeeded" | "failed", error?: unknown): void => {
    if (recorded || op.type === "subscription") {
      return;
    }
    recorded = true;
    const durationMs = Math.round(performance.now() - startedAt);

    if (outcome === "succeeded") {
      if (op.type === "mutation") {
        captureAnalyticsEvent("dashboard_action_succeeded", {
          procedure: op.path,
          duration_ms: durationMs,
        });
      }
      return;
    }

    const errorCode = getErrorCode(error);
    if (UNTRACKED_ERROR_CODES.has(errorCode)) {
      return;
    }
    captureAnalyticsEvent(
      op.type === "mutation"
        ? "dashboard_action_failed"
        : "dashboard_query_failed",
      {
        procedure: op.path,
        duration_ms: durationMs,
        error_code: errorCode,
      },
    );
  };
}

function forwardWithAnalytics<TRouter extends AnyRouter>(
  { op, next }: Parameters<OperationLink<TRouter>>[0],
  observer: Observer<
    OperationResultEnvelope<unknown, TRPCClientError<TRouter>>,
    TRPCClientError<TRouter>
  >,
) {
  const record = createOutcomeRecorder(op);
  return next(op).subscribe({
    next: (value) => {
      record("succeeded");
      observer.next(value);
    },
    error: (error) => {
      record("failed", error);
      observer.error(error);
    },
    complete: () => observer.complete(),
  });
}

/**
 * Records what engineers experienced: every mutation outcome with its latency, and every failed query.
 * Successful queries are not sent because polling would drown the signal. Only the procedure name, error code
 * and duration leave the browser, never the input or response.
 */
export function analyticsLink<TRouter extends AnyRouter>(): TRPCLink<TRouter> {
  return () => (args) =>
    observable((observer) => forwardWithAnalytics<TRouter>(args, observer));
}
